import { message, Modal, Select, Spin, Table, Tabs } from "antd";
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import WithAuth from "../../hoc/WithAuth";
import { UserOutlined, FileOutlined } from "@ant-design/icons";
import OrderCard from "../../components/cards/order-card/OrderCard";
import {
  useAddFinancialInstituteToOrder,
  useAddVCEOrder,
  useGetVCEByOrderId,
  useOrderDetail,
  useRemoveBankFromOrder,
  useRemoveVCEOrder,
} from "../../api/order";
import DueDateCard from "../../components/cards/due-date-card/DueDateCard";
import { useCustomerListByOrderId } from "../../api/customer";
import TableAction from "../../components/TableAction";
import {
  useContractByOrderIdList,
  useContractListByOrderId,
} from "../../api/contract";
import { getStatusColor } from "../../util/statusColor";
import VCETable from "../../components/tables/order/VCETable";
import AgroDealerTable from "../../components/tables/order/AgroDealers";
import OrderTrackingGraph from "../../components/OrderTrackingGraph";
import { useGetFarmingStageByOrderId } from "../../api/farming_stage";
import BanksTable from "../../components/tables/order/BanksTable";
import { FinancialInstituteSelect } from "../../components/form/FinancialInstituteSelect";
import {
  useAssignOrderToAgroDealer,
  useGetAgroDealersByOrderId,
  useGetAgroDealersList,
  useRemoveAgroDealerFromOrder,
} from "../../api/agro_dealer";

const container: CSSProperties = {
  fontFamily: "Poppins",
  borderRadius: 8.5,
  marginTop: 25,
  backgroundColor: "white",
};

const contractsColumn = [
  {
    title: "No.",
    width: 60,
    render: (text: string, record: any, index: number) => <b>{index + 1}</b>,
  },
  {
    dataIndex: "contractNumber",
    title: "Contract No.",
  },
  {
    dataIndex: "fullName",
    title: "Customer name",
  },
  {
    dataIndex: "product",
    title: "Product",
  },
  {
    dataIndex: "quantity",
    title: "Quantity",
  },
  {
    dataIndex: "contractStatus",
    title: "Status",
    render: (status: string) => {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "80%",
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 8.5,
            color: getStatusColor(status),
            backgroundColor: `${getStatusColor(status)}44`,
          }}
        >
          {status}
        </div>
      );
    },
  },
];

function OrderDetailPage() {
  const { id } = useParams();
  const [userId, setUserId] = useState<string>();
  const [open, setOpen] = useState(false);
  const [openAdModal, setOpenAdModal] = useState(false);
  const [openBanks, setOpenBanks] = useState(false);
  const [removeopen, setRemoveOpen] = useState(false);
  const [removeAdOpen, setRemoveAdOpen] = useState(false);
  const [removeBankOpen, setRemoveBankOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedAdId, setSelectedAdId] = useState<string>("");
  const [selectedBankId, setSelectedBankId] = useState<string>("");

  const [adId, setAdId] = useState<string | undefined>();
  const [bankId, setBankId] = useState<string | undefined>();

  const [contractsTableData, setContractsTableData] = useState<any[]>([]);
  const {
    data: _contracts,
    isLoading: contractLoading,
    refetch: refetchContracts,
  } = useContractByOrderIdList(id);
  const {
    data: vces,
    isLoading: vceLoading,
    refetch: refetchVce,
  } = useGetVCEByOrderId(id);
  const { data: a_dealrs, isLoading: adLoading } = useGetAgroDealersByOrderId(
    id!,
  );
  const { data: all_agro_dealers } = useGetAgroDealersList();
  const { mutateAsync: assignAgroDealer, isPending: assigningAD } =
    useAssignOrderToAgroDealer();
  const { mutateAsync: removeAgroDealer, isPending: removingAd } =
    useRemoveAgroDealerFromOrder();
  const { mutateAsync: assignBank, isPending: addingBank } =
    useAddFinancialInstituteToOrder();
  const { mutateAsync: removeBankFromOrder, isPending: removingBank } =
    useRemoveBankFromOrder();

  const { mutateAsync: assignVCE, isPending } = useAddVCEOrder();

  const { mutateAsync: removeVCE, isPending: removing } = useRemoveVCEOrder();
  const [contracts, setContracts] = useState(0);
  const [customers, setCustomers] = useState(0);

  const [tableIndex, setTableIndex] = useState<number>(0);
  const [contractsSearchText, setContractsSearchText] = useState<string>("");

  const { data: contractsData, refetch } = useContractListByOrderId(id);
  const { data: customersData } = useCustomerListByOrderId(id || "");
  const [graphData, setGraphData] = useState<any>([]);
  const { data: trackingData, refetch: refetchFarmingStageData } =
    useGetFarmingStageByOrderId(id);

  const { data: order, isLoading } = useOrderDetail(id || "");
  const [activeTab, setActiveTab] = useState("1");
  useEffect(() => {
    if (id) {
      refetch();
      refetchFarmingStageData();
    }
  }, [id]);

  useEffect(() => {
    if (contractsSearchText) {
      if (contractsData && contractsData?.data?.content) {
        const _data = contractsData.data.content.filter(
          (item: any) =>
            item.contractNumber
              ?.toLowerCase()
              .includes(contractsSearchText.toLowerCase()) ||
            item.fullName
              ?.toLowerCase()
              .includes(contractsSearchText.toLowerCase()) ||
            item.product
              ?.toLowerCase()
              .includes(contractsSearchText.toLowerCase()) ||
            item.contractStatus
              ?.toLowerCase()
              .includes(contractsSearchText.toLowerCase()),
        );

        setContractsTableData(_data);
      }
    } else {
      setContractsTableData(contractsData?.data?.content);
    }
  }, [contractsSearchText, contractsData]);

  useEffect(() => {
    if (contractsData && contractsData?.data?.content) {
      setContracts(contractsData.data.content.length);

      const _customerIds: number[] = contractsData.data.content.map(
        (item: any) => item.customerId,
      );
      const _customers = Array.from(new Set(_customerIds));
      setCustomers(_customers.length);
    }
  }, [contractsData]);

  useEffect(() => {
    if (
      trackingData &&
      trackingData.data &&
      trackingData.data.data &&
      trackingData.data.data.data
    ) {
      const val = Object.entries(trackingData.data.data.data).map((v: any) => {
        return { date: v[0], ...v[1] };
      });
      if (val) {
        setGraphData(val);
      }
    }
  }, [trackingData]);

  const customersColumn = [
    {
      title: "No.",
      width: 60,
      render: (text: string, record: any, index: number) => <b>{index + 1}</b>,
    },
    {
      dataIndex: "fullName",
      title: "Full name",
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: 400,
              fontSize: 15,
              gap: 12,
            }}
          >
            {record.fullName}
          </div>
        );
      },
    },
    {
      dataIndex: "birthDate",
      title: "Birth date",
    },
    {
      dataIndex: "gender",
      title: "Gender",
    },
    {
      dataIndex: "phoneNumber",
      title: "Phone No.",
    },
    {
      dataIndex: "address",
      title: "Address",
      render: (address: string, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: 400,
              fontSize: 15,
              gap: 12,
            }}
          >
            {Object.entries(JSON.parse(address))
              .map((x) => x[1])
              .join(" ")}
          </div>
        );
      },
    },
  ];

  const renderTable = (): ReactNode => {
    switch (tableIndex) {
      case 0:
        return (
          <Table
            dataSource={customersData?.data || []}
            columns={customersColumn}
            loading={isLoading}
          />
        );
        break;
      case 1:
        return (
          <Table
            dataSource={contractsData?.data?.content || []}
            columns={contractsColumn}
            loading={isLoading}
          />
        );
        break;
      default:
        return (
          <Table
            dataSource={customersData?.data || []}
            columns={customersColumn}
            loading={isLoading}
          />
        );
        break;
    }
  };

  const onSubmit = async () => {
    if (userId && !isNaN(Number(id))) {
      const result = await assignVCE({
        orderId: Number(id),
        userId: Number(userId),
      });
      setOpen(false);
    }
  };

  const onRemove = async () => {
    if (selectedId) {
      try {
        const result = await removeVCE(Number(selectedId));
        if (result.status === 2000) {
          message.success("Value-chain-executor removed successfully.");
        }
      } catch (err: any) {
        message.error("operation failed");
      } finally {
        setRemoveOpen(false);
      }
    }
  };

  const onAddInputSupplier = async () => {
    if (adId && id) {
      try {
        const result = await assignAgroDealer({
          orderId: Number(id),
          orgId: Number(adId),
        });

        if (result.data.status !== "FAILURE") {
          message.success("Agro-dealer assigned successfully.");
        } else {
          message.error("Operation failed.");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        setOpen(false);
      }
    }
  };

  const onRemoveInputSupplier = async () => {
    if (selectedAdId) {
      try {
        const result = await removeAgroDealer(Number(selectedAdId));

        if (result.status === 200) {
          message.success("Agro-dealer assigned successfully.");
        } else {
          message.error("Operation failed.");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        setRemoveOpen(false);
      }
    }
  };
  const onBankAdd = async () => {
    if (bankId && id) {
      try {
        const result = await assignBank({
          orderId: Number(id),
          orgId: Number(bankId),
        });

        if (result.status === 200) {
          message.success("Financial institute assigned successfully.");
        } else {
          message.error("Operation failed.");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        setOpenBanks(false);
      }
    }
  };

  const onRemoveBank = async () => {
    if (selectedBankId) {
      try {
        const result = await removeBankFromOrder(Number(selectedBankId));

        if (result.status === 200) {
          message.success("Financial institute assigned successfully.");
        } else {
          message.error("Operation failed.");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        setRemoveOpen(false);
      }
    }
  };

  const tabItems = [
    {
      key: "1",
      label: "FARMING STATUS",
      children: (
        <div style={container}>
          <div
            style={{
              backgroundColor: "#E9EDF4",
              padding: "15px",
              borderRadius: "8.5px",
              fontWeight: 500,
              fontSize: "24px",
              marginBottom: "30px",
            }}
          >
            Farming Status
          </div>
          <OrderTrackingGraph data={graphData} />
        </div>
      ),
    },
    // {
    //   key: "2",
    //   label: "MAP",
    //   children: (
    //     <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    //       {id &&  <Map orderId={id} />}
    //     </div>
    //   ),
    // },
    {
      key: "2",
      label: "CONTRACTS",
      children: (
        <div style={container}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
              backgroundColor: "#eef8ef",
              borderRadius: 8.4,
              marginBottom: 22,
              fontFamily: "Poppins",
              fontSize: 22,
            }}
          >
            Contracts
          </div>
          <TableAction setSearchText={setContractsSearchText} />
          <Table
            dataSource={contractsTableData}
            columns={contractsColumn}
            loading={isLoading}
          />
        </div>
      ),
    },
    {
      key: "3",
      label: "VALUE CHAIN EXECUTORS",
      children: (
        <div
          className="hidden md:block"
          style={{
            borderRadius: 8.5,
            marginTop: 25,
            paddingLeft: 50,
            paddingRight: 50,
            paddingTop: 22,
            backgroundColor: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
              backgroundColor: "#EEF3F8",
              borderRadius: 8.4,
              marginBottom: 22,
              fontFamily: "Poppins",
              fontSize: 22,
            }}
          >
            Value chain executors
          </div>
          {id && <VCETable orderId={id} />}
        </div>
      ),
    },
    {
      key: "4",
      label: "AGRO DEALERS",
      children: (
        <div
          className="hidden md:block"
          style={{
            borderRadius: 8.5,
            marginTop: 25,
            paddingLeft: 50,
            paddingRight: 50,
            paddingTop: 22,
            backgroundColor: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
              backgroundColor: "#EEF3F8",
              borderRadius: 8.4,
              marginBottom: 22,
              fontFamily: "Poppins",
              fontSize: 22,
            }}
          >
            Agro Dealers
          </div>
          {id && <AgroDealerTable orderId={id} />}
        </div>
      ),
    },
    {
      key: "5",
      label: "FINANCIAL INSTITUTES",
      children: (
        <div
          className="hidden md:block"
          style={{
            borderRadius: 8.5,
            marginTop: 25,
            paddingLeft: 50,
            paddingRight: 50,
            paddingTop: 22,
            backgroundColor: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 80,
              backgroundColor: "#EEF3F8",
              borderRadius: 8.4,
              marginBottom: 22,
              fontFamily: "Poppins",
              fontSize: 22,
            }}
          >
            Financial Institutes
          </div>
          {id && <BanksTable orderId={id} />}
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={removeAdOpen}
        title={<div className="text-black">Remove</div>}
        onOk={onRemoveInputSupplier}
        onCancel={() => setRemoveAdOpen(false)}
        okButtonProps={{
          disabled: removing,
          loading: removing,
        }}
      >
        <div className="text-black">
          Are you sure you want to remove the selected agro-dealer from this
          order?
        </div>
      </Modal>

      <Modal
        open={removeopen}
        title={<div className="text-black">Remove</div>}
        onOk={onRemove}
        onCancel={() => setRemoveOpen(false)}
        okButtonProps={{
          disabled: removing,
          loading: removing,
        }}
      >
        <div className="text-black">
          Are you sure you want to remove the selected value-chain-executer from
          this order?
        </div>
      </Modal>
      <Modal
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 27.43,
              color: "black",
            }}
          >
            Select value chain executor
          </div>
        }
        destroyOnClose={true}
        open={open}
        centered
        okButtonProps={{
          disabled: userId === undefined || isPending,
          loading: isPending,
        }}
        onOk={onSubmit}
        onCancel={() => setOpen(false)}
        closable
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select
            value={userId}
            placeholder="Select value chain executor"
            style={{ width: "100%" }}
            onChange={(val: any) => setUserId(val)}
            options={vces?.data?.data?.map((user: any) => {
              return { label: user.fullName, value: user.vceId };
            })}
          />
        </div>
      </Modal>
      <Modal
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 27.43,
              color: "black",
            }}
          >
            Select agro dealer
          </div>
        }
        open={openAdModal}
        centered
        onOk={onAddInputSupplier}
        okButtonProps={{
          disabled: adId === undefined || assigningAD,
          loading: assigningAD,
        }}
        onCancel={() => setOpenAdModal(false)}
        closable
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select
            value={adId}
            placeholder="Select agro dealer"
            style={{ width: "100%" }}
            onChange={(val: any) => setAdId(val)}
            options={all_agro_dealers?.data?.data?.map((ad: any) => {
              return { label: ad.name, value: ad.id };
            })}
          />
        </div>
      </Modal>
      <Modal
        open={removeBankOpen}
        title={<div className="text-black">Remove</div>}
        onOk={onRemoveBank}
        onCancel={() => setRemoveBankOpen(false)}
        okButtonProps={{
          disabled: removing,
          loading: removing,
        }}
      >
        <div className="text-black">
          Are you sure you want to remove the selected bank from this order?
        </div>
      </Modal>

      <Modal
        destroyOnClose={true}
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 27.43,
              color: "black",
            }}
          >
            Select financial institute
          </div>
        }
        open={openBanks}
        centered
        onOk={onBankAdd}
        okButtonProps={{
          disabled: bankId === undefined || isPending,
          loading: isPending,
        }}
        onCancel={() => setOpenBanks(false)}
        closable
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <FinancialInstituteSelect
            value={bankId}
            onChange={(val) => setBankId(val)}
          />
        </div>
      </Modal>

      <div className="flex flex-col gap-[12px] mt-4">
        <div className="flex flex-col md:flex-row justify-between gap-[20px]">
          {order && order.data && order.data.data && (
            <OrderCard
              off_taker={order.data.data.orgName}
              start_date={order.data.data.startingDate}
              due_date={order.data.data.dueDate}
              product={order.data.data.product}
              quantity={order.data.data.quantity}
              status={order.data.data.status}
            />
          )}
          {(!order || !order.data || !order.data.data) && (
            <div className="w-[80%] bg-white rounded-xl flex flex-col gap-5 justify-center items-center">
              <div className="text-2xl text-[#009602]">
                Order detail loading...
              </div>
              <Spin />
            </div>
          )}
          {order && order.data && (
            <DueDateCard
              dueDate={order.data.data.deliveryDays}
              product={order.data.data.product}
            />
          )}
          <div className="flex flex-row md:flex-col gap-[20px]">
            <div
              className="flex flex-col w-1/2 md:w-full border-2 rounded-xl bg-white h-[160px]"
              onClick={() => setTableIndex(0)}
            >
              <div className="flex justify-between items-center px-4 md:px-8 pt-4">
                <div className="text-black text-xl md:text-2xl">Farmers</div>
                <UserOutlined
                  style={{ color: "#268D60", fontSize: 40 }}
                  onClick={() => setTableIndex(0)}
                />
              </div>
              <div className="w-full h-full flex justify-center items-center font-semibold text-4xl">
                {customers}
              </div>
            </div>
            <div
              className="flex flex-col w-1/2 md:w-full border-2 rounded-xl bg-white h-[160px]"
              onClick={() => setTableIndex(0)}
            >
              <div className="flex justify-between items-center px-4 md:px-8 pt-4">
                <div className="text-black text-xl md:text-2xl">Contracts</div>
                <FileOutlined
                  style={{ color: "#268D60", fontSize: 40 }}
                  onClick={() => setTableIndex(1)}
                />
              </div>
              <div className="w-full h-full flex justify-center items-center font-semibold text-4xl">
                {contracts}
              </div>
            </div>
          </div>
        </div>
        <Tabs
          style={{
            background: "white",
            fontWeight: 700,
            paddingLeft: 20,
          }}
          className="text-black"
          defaultActiveKey="1"
          items={tabItems}
          onChange={(key) => setActiveTab(key)}
        />
      </div>
    </>
  );
}

export default WithAuth(OrderDetailPage);
